import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Skeleton } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminQuery } from '../api'
import type { Page } from '../useCollection'
import type { VoteBallotRow, VoteResults } from '../types'

/**
 * Le dépouillement d'un scrutin, réservé à l'équipe.
 *
 * C'est ici qu'on prépare l'annonce **sans la rendre publique** : les scores
 * sont lisibles dès le premier bulletin, alors que la salle ne les verra qu'à
 * la publication. Et c'est ici qu'on annule un bulletin frauduleux — toujours
 * avec un motif, jamais en silence : un scrutin corrigé doit rester relisible.
 */
export function VoteResultsPanel({
  eventId,
  can,
  categoryId,
  nameOf,
}: {
  /** L'édition et les droits : passés par le parent, back-office ou conducteur. */
  eventId: string | undefined
  can: (permission: string) => boolean
  categoryId: string
  /** Nom affichable d'un candidat, à partir de son identifiant. */
  nameOf: (candidateId: string | null) => string
}) {
  const { t, i18n } = useTranslation()
  const [showBallots, setShowBallots] = useState(false)
  const [target, setTarget] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const base = `/admin/events/${eventId}`
  const results = useAdminQuery<VoteResults>(
    ['admin-vote-results', categoryId],
    `${base}/vote-categories/${categoryId}/results`,
    eventId,
    { enabled: can('voting.result.read') },
  )
  const ballots = useAdminQuery<Page<VoteBallotRow>>(
    ['admin-vote-ballots', categoryId],
    `${base}/votes?category_id=${categoryId}&page_size=200`,
    eventId,
    { enabled: showBallots && can('voting.result.read') },
  )
  const invalidate = useAdminMutation<VoteBallotRow, { id: string; reason: string }>(
    'post',
    (variables) => `${base}/votes/${variables.id}/invalidate`,
    eventId,
    [
      ['admin-vote-results', categoryId],
      ['admin-vote-ballots', categoryId],
      ['admin-standings'],
    ],
  )

  if (!can('voting.result.read')) return null

  const data = results.data
  const formatter = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

  return (
    <div className="mt-8 border-t border-line-subtle pt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.voteResults.title')}
        </p>
        <Button size="sm" variant="ghost" onClick={() => void results.refetch()}>
          {t('admin.voteResults.refresh')}
        </Button>
      </div>
      <p className="mt-1 text-xs text-content-muted">{t('admin.voteResults.hint')}</p>

      {results.isPending ? <Skeleton className="mt-3 h-24 w-full" /> : null}
      {results.isError ? (
        <p className="mt-3 text-sm text-state-danger">{results.error.message}</p>
      ) : null}

      {data ? (
        <>
          <p className="mt-3 text-sm text-content-secondary">
            {t('admin.voteResults.summary', {
              votes: data.total_votes,
              voters: data.distinct_voters,
              invalidated: data.invalidated_votes,
            })}
          </p>
          {data.scores.length === 0 ? (
            <p className="mt-2 text-sm text-content-muted">{t('admin.voteResults.empty')}</p>
          ) : (
            <ol className="mt-3 space-y-2">
              {data.scores.map((score, index) => (
                <li key={score.candidate_id ?? score.attendee_id ?? index}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate text-content-primary">
                      {score.candidate_number !== null ? (
                        <span className="mr-2 font-mono text-caption text-content-muted">
                          #{score.candidate_number}
                        </span>
                      ) : null}
                      {score.name}
                    </span>
                    <span className="shrink-0 tabular-nums text-champagne-300">
                      {score.votes} · {Math.round(score.share)} %
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-bg-overlay">
                    <div
                      className="h-full rounded-full bg-champagne-400/70 transition-[width] duration-700"
                      style={{ width: `${Math.min(100, Math.max(0, score.share))}%` }}
                    />
                  </div>
                </li>
              ))}
            </ol>
          )}
        </>
      ) : null}

      <Button
        size="sm"
        variant="outline"
        className="mt-4"
        onClick={() => setShowBallots((value) => !value)}
      >
        {showBallots ? t('admin.voteResults.hideBallots') : t('admin.voteResults.showBallots')}
      </Button>

      {showBallots ? (
        <div className="mt-3">
          {ballots.isPending ? <Skeleton className="h-24 w-full" /> : null}
          {ballots.data && ballots.data.items.length === 0 ? (
            <p className="text-sm text-content-muted">{t('admin.voteResults.noBallots')}</p>
          ) : null}
          {ballots.data && ballots.data.total > ballots.data.items.length ? (
            <p className="mb-2 text-xs text-content-muted">
              {t('admin.voteResults.truncated', {
                shown: ballots.data.items.length,
                total: ballots.data.total,
              })}
            </p>
          ) : null}
          <ul className="divide-y divide-line-subtle rounded-md border border-line-subtle">
            {ballots.data?.items.map((ballot) => (
              <li key={ballot.id} className="px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span
                    className={cn(
                      'min-w-0',
                      ballot.invalidated_at
                        ? 'text-content-muted line-through'
                        : 'text-content-primary',
                    )}
                  >
                    <span className="mr-2 font-mono text-caption text-content-muted">
                      {formatter.format(new Date(ballot.created_at))}
                    </span>
                    {nameOf(ballot.candidate_id)}
                    <span className="ml-2 font-mono text-caption text-content-muted">
                      {t('admin.voteResults.voter', { id: ballot.voter_attendee_id.slice(0, 8) })}
                    </span>
                  </span>
                  {ballot.invalidated_at ? (
                    <span className="text-caption text-state-warning">
                      {t('admin.voteResults.invalidated', {
                        reason: ballot.invalidation_reason ?? '',
                      })}
                    </span>
                  ) : can('voting.vote.invalidate') ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setTarget(ballot.id)
                        setReason('')
                      }}
                    >
                      {t('admin.voteResults.invalidate')}
                    </Button>
                  ) : null}
                </div>
                {target === ballot.id ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder={t('admin.voteResults.reasonPlaceholder')}
                      className="min-w-0 flex-1 rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary"
                    />
                    <Button
                      size="sm"
                      variant="ember"
                      disabled={reason.trim().length < 5 || invalidate.isPending}
                      onClick={() =>
                        invalidate.mutate(
                          { id: ballot.id, reason: reason.trim() },
                          { onSuccess: () => setTarget(null) },
                        )
                      }
                    >
                      {t('admin.voteResults.confirmInvalidate')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setTarget(null)}>
                      {t('admin.cancel')}
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          {invalidate.error instanceof ApiError ? (
            <p className="mt-2 text-sm text-state-danger">{invalidate.error.message}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
